import { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import moment from 'moment'
import { F0Box } from '@factorialco/f0-react'

import {
  EngagementSurveySettingsDocument,
  UpdateSurveySettingsDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { scheduleStartDateFromUTC } from 'modules/engagement_surveys/lib/getScheduleStartDate'
import { useSurveyActions } from 'modules/engagement_surveys/hooks/useSurveyActions'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import { SurveySettingsForm, type SurveySettingsFormValues } from './form'

import { UpdateHeader } from './UpdateHeader'

const mapScheduleRecurrenceType = (
  recurrenceType: string | null | undefined
): SurveySettingsFormValues['recurrenceType'] => {
  switch (recurrenceType) {
    case 'daily':
    case 'weekly':
    case 'monthly':
    case 'quarterly':
    case 'yearly':
      return recurrenceType
    default:
      return 'onetime'
  }
}

export default function SurveySettingsPage() {
  const { surveyId: surveyIdParam } = useParams<{ surveyId: string }>()
  const surveyId = Number(surveyIdParam)

  const { data: queryData, loading } = useQuery(EngagementSurveySettingsDocument, {
    variables: { surveyId },
    skip: !surveyId,
  })

  const [updateSurveySettings] = useMutation(UpdateSurveySettingsDocument, {
    extractorFn: (mutationData) => mutationData.surveysMutations.updateSurveysSurvey,
  })

  const survey = queryData?.surveys.survey
  const surveyForm = survey?.form
  const schedule = surveyForm?.schedule
  const respondentStrategy = survey?.respondentStrategy

  const defaultValues: () => Partial<SurveySettingsFormValues> = () => {
    const editorIds =
      survey?.editors
        ?.map((editor) => editor?.employee?.id)
        .filter((id): id is number => id !== undefined && id !== null) ?? []

    const participantIds = (respondentStrategy?.arguments ?? []).filter(
      (id): id is number => id !== null && id !== undefined
    )

    const respondentStrategyArguments = respondentStrategy?.arguments ?? []

    const teams = respondentStrategy?.strategy === 'teams' ? respondentStrategyArguments : undefined
    const legalEntities =
      respondentStrategy?.strategy === 'legal_entities' ? respondentStrategyArguments : undefined
    const locations =
      respondentStrategy?.strategy === 'locations' ? respondentStrategyArguments : undefined

    // Read start date from schedule
    const startDate = schedule?.startDate ? scheduleStartDateFromUTC(schedule.startDate) : undefined

    const strategy = respondentStrategy?.strategy ?? 'all_employees'

    return {
      title: surveyForm?.title || '',
      description: surveyForm?.description || '',
      restrictedViewersEnabled: survey?.restrictedViewersEnabled || false,
      anonymous: surveyForm?.anonymous || false,
      participantStrategy: strategy,
      participants:
        strategy === 'employees' ? { type: 'manual' as const, ids: participantIds } : undefined,
      editorIds: {
        type: 'manual' as const,
        ids: editorIds,
      },
      teams,
      legalEntities,
      locations,
      startDate: startDate?.toDate() || undefined,
      endsAt: surveyForm?.endsAt ? new Date(surveyForm.endsAt) : undefined,
      recurrenceType: mapScheduleRecurrenceType(schedule?.recurrenceType),
      daysToAnswer: schedule?.daysToAnswer || undefined,
    }
  }

  const handleSubmit = useCallback(
    async (values: SurveySettingsFormValues) => {
      const isEmployeeStrategy = values.participantStrategy === 'employees'

      let respondentStrategyArguments: number[] = []
      if (values.participantStrategy === 'teams') {
        respondentStrategyArguments = values.teams ?? []
      }

      if (values.participantStrategy === 'legal_entities') {
        respondentStrategyArguments = values.legalEntities ?? []
      }

      if (values.participantStrategy === 'locations') {
        respondentStrategyArguments = values.locations ?? []
      }

      const respondentStrategyInput = {
        strategy: values.participantStrategy,
        arguments: respondentStrategyArguments,
      }

      const respondentEmployeeSelection = isEmployeeStrategy ? values.participants : undefined
      const editorSelection = values.editorIds

      // Build schedule input from form values
      const startDateMoment = values.startDate ? moment(values.startDate) : null
      const scheduleInput = {
        startDate: startDateMoment?.format('YYYY-MM-DD') ?? null,
        startTime: startDateMoment?.format('HH:mm') ?? null,
        recurrenceType: values.recurrenceType ?? 'onetime',
        daysToAnswer: values.daysToAnswer ?? null,
      }

      const result = await updateSurveySettings({
        variables: {
          surveyId,
          title: values.title,
          description: values.description,
          anonymous: values.anonymous,
          restrictedViewersEnabled: values.restrictedViewersEnabled,
          editorIds: [],
          editorSelection,
          respondentStrategy: respondentStrategyInput,
          respondentEmployeeSelection,
          endsAt: values.endsAt ?? null,
          schedule: scheduleInput,
        },
      })

      const handled = handleResult(result, () => {})

      if (!handled.success) {
        return {
          success: false,
          rootMessage: i18n.t('surveys.settings.error_message'),
        }
      }

      app.setFlash({
        type: 'correct',
        message: i18n.t('surveys.settings.success_message'),
        time: 1000,
      })

      return { success: true }
    },
    [surveyId, updateSurveySettings]
  )

  const isDraft = survey?.form?.status === 'draft'
  const isLoading = loading && !survey
  const formId = surveyForm?.id.toString() || ''

  const { handleActionClick } = useSurveyActions({ formId })

  return (
    <ResponsivePage.Body
      loading={isLoading}
      error={undefined}
      onActionClick={handleActionClick}
      variant="fullscreen"
    >
      <F0Box display="flex" justifyContent="center" paddingBottom="5xl">
        <F0Box paddingBottom="2xl">
          <SurveySettingsForm
            defaultValues={defaultValues()}
            onSubmit={handleSubmit}
            disablePublishingFields={!isDraft}
          />
        </F0Box>
        <UpdateHeader title={surveyForm?.title || ''} description={surveyForm?.description || ''} />
      </F0Box>
    </ResponsivePage.Body>
  )
}
